import React, { useEffect, useRef, useState } from 'react';
import { getIndices } from '../services/marketdata';

/**
 * Site-wide index ticker.
 *
 * Polls the PUBLIC /marketdata/indices endpoint, which is served from a short
 * server-side cache. It is a REST poll rather than a WebSocket on purpose: this
 * renders for logged-out visitors, and the realtime socket requires an
 * authenticated ticket. A marketing surface does not justify exposing an
 * unauthenticated stream, and 30s is ample here.
 *
 * Falls back to the previous static values until the first response arrives, so
 * the bar never renders empty or collapses the layout.
 */

const POLL_MS = 30000;

// Display names for the bare symbols the API returns.
const LABELS = {
  NIFTY: 'NIFTY 50',
  BANKNIFTY: 'BANK NIFTY',
  SENSEX: 'SENSEX',
  INDIAVIX: 'INDIA VIX',
};

// Shown before the first successful fetch. Marked as placeholder rather than
// labelled "LIVE" — the previous version presented these as live indefinitely.
const PLACEHOLDER = [
  { name: 'NIFTY 50', price: '—', change: '', up: true },
  { name: 'BANK NIFTY', price: '—', change: '', up: true },
  { name: 'SENSEX', price: '—', change: '', up: true },
  { name: 'INDIA VIX', price: '—', change: '', up: false },
];

function fmtPrice(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtChange(pct) {
  if (pct == null || !Number.isFinite(Number(pct))) return '';
  const n = Number(pct);
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

const TickerBar = () => {
  const [items, setItems] = useState(PLACEHOLDER);
  const [live, setLive] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await getIndices();
        if (cancelled || !res.data?.success) return;
        const rows = res.data.data?.indices || [];
        if (rows.length === 0) return;

        setItems(rows.map(r => ({
          name: LABELS[r.symbol] || r.symbol,
          price: fmtPrice(r.ltp),
          change: fmtChange(r.change_pct),
          up: Number(r.change_pct) >= 0,
        })));
        // "degraded" means the request fell back to stored candles upstream.
        setLive(!res.data.data?.degraded);
      } catch {
        // Keep whatever is on screen; a failed poll must not blank the bar.
      }
    };

    load();
    timer.current = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  // Duplicated for the seamless marquee loop.
  const track = [...items, ...items];

  return (
    <div className="ticker-bar">
      <div className="ticker-label">{live ? 'LIVE' : 'MARKET'}</div>
      <div className="ticker-scroll">
        <div className="ticker-track">
          {track.map((t, i) => (
            <div key={i} className="ticker-item">
              <span className="t-name">{t.name}</span>
              <span className="t-price">{t.price}</span>
              {t.change && <span className={t.up ? 't-up' : 't-dn'}>{t.change}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TickerBar;
